import * as React from 'react'
import { observer } from 'mobx-react'
import Icon from 'components/Icon'
import {
  TableHeader,
  TableHost,
  TableEmptyStateContainer,
  ImageCell,
  DateCell,
  TableCell,
  TextCell,
} from 'components/Table'
import {
  TableSearchbar,
  TableSearchbarMenuItem,
} from 'payouts/components/TableSearchbar'
import { EmptyState, EmptyStateIllustrationImage } from 'components/EmptyState'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownItemTitle,
} from 'components/Dropdown'
import { IconButton } from 'components/IconButton'
import Button from 'components/Button'
import ContentError from 'components/ContentError'
import FilterControlsToggleButton from 'components/FilterControlsToggleButton'
import {
  ContextCell,
  AmountCell,
} from 'payouts/components/PayoutTransactionsTableCells'
import FilterControls from 'infra/FilterControls'
import { useToggle } from 'hooks/state-hooks'
import VirtualTable from 'payouts/components/VirtualTable'
import { ProPayoutsViewModel } from 'payouts/pro/ProPayoutsViewModel'

interface ProHistoricalTransactionsTableProps {
  vm: ProPayoutsViewModel
}

/**
 * The ProHistoricalTransactionsTable
 */
export default observer(function ProHistoricalTransactionsTable({
  vm,
}: ProHistoricalTransactionsTableProps) {
  const isLoading = vm.transactionsScroller.fetch.pending
  const shouldShowEmptyState = vm.transactions.length === 0
  const filtersShowing = useToggle(false)

  return (
    <>
      <TableHost>
        <TableSearchbar
          searchText={vm.searchText.value}
          onSearchTextChange={vm.searchText.change}
        >
          <TableSearchbarMenuItem>
            <FilterControlsToggleButton
              active={filtersShowing.isEnabled}
              count={vm.filters.appliedFilterCount}
              onClick={filtersShowing.toggle}
            />
          </TableSearchbarMenuItem>
          <TableSearchbarMenuItem>
            <Dropdown
              autoDismiss
              button={
                <IconButton>
                  <Icon name="more" />
                </IconButton>
              }
            >
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                disabled={vm.exportCsv.pending}
                icon={<Icon name="download" />}
                onClick={vm.exportCsv}
              >
                <DropdownItemTitle>Download CSV</DropdownItemTitle>
              </DropdownItem>
            </Dropdown>
          </TableSearchbarMenuItem>
        </TableSearchbar>
        <FilterControls vm={vm.filters} active={filtersShowing.isEnabled} />

        {isLoading || !shouldShowEmptyState ? (
          <VirtualTable
            totalCount={vm.transactions.length}
            overscan={400}
            scroller={vm.transactionsScroller}
            itemContent={(index) => {
              const tvm = vm.transactions[index]
              return (
                <>
                  <DateCell
                    date={tvm.transaction.completedAt}
                    dataTest={`pro-historical-transaction-date-completed-${tvm.transaction.id}`}
                  />
                  <ImageCell
                    title={tvm.transaction.workspace.name}
                    image={tvm.transaction.workspace.branding.logos.icon}
                    minWidth={110}
                  />
                  <ContextCell
                    source={tvm.transaction.source}
                    userTransactionMemo={tvm.transaction.memo}
                    userTransactionReason={tvm.transaction.reason}
                    jobTransactionJobId={tvm.transaction.job?.shortId}
                  />
                  <TextCell
                    dataTest={`pro-historical-transaction-job-name-${tvm.transaction.id}`}
                  >
                    {tvm.transaction.job?.displayName}
                  </TextCell>
                  <AmountCell neutral amount={tvm.transaction.amount} />
                  <TableCell align="right">
                    {tvm.transaction.job && (
                      <Dropdown
                        autoDismiss
                        button={
                          // Needs to be inline, otherwise it doesn't align
                          // correctly with the `more` button in the searchbar.
                          <IconButton inline>
                            <Icon name="more" />
                          </IconButton>
                        }
                      >
                        <DropdownTitle>Actions</DropdownTitle>
                        <DropdownItem
                          onClick={tvm.viewJob}
                          icon={<Icon name="view" />}
                        >
                          View job
                        </DropdownItem>
                      </Dropdown>
                    )}
                  </TableCell>
                </>
              )
            }}
            headerContent={() => (
              <>
                <TableHeader align="left">Date</TableHeader>
                <TableHeader align="left">Workspace</TableHeader>
                <TableHeader align="left">Type</TableHeader>
                <TableHeader align="left">Job name</TableHeader>
                <TableHeader align="right">Amount</TableHeader>
                <TableHeader align="right"></TableHeader>
              </>
            )}
          />
        ) : vm.transactionsScroller.fetch.error ? (
          <ContentError large error={vm.transactionsScroller.fetch.error} />
        ) : (
          <HistoricalTransactionsEmptyState
            appliedFilters={vm.filtersApplied}
            onClearFilters={vm.clearFilters}
          />
        )}
      </TableHost>
    </>
  )
})

/**
 * Empty state.
 */
function HistoricalTransactionsEmptyState({
  appliedFilters,
  onClearFilters,
}: {
  appliedFilters: boolean
  onClearFilters: () => void
}) {
  return (
    <TableEmptyStateContainer bordered>
      {appliedFilters ? (
        <EmptyState
          title="We couldn't find what you were looking for."
          subtitle={
            <>
              Maybe try{' '}
              <Button size="baseline" type="text" onClick={onClearFilters}>
                clearing your search filter
              </Button>
              ?
            </>
          }
          small
          image={
            <EmptyStateIllustrationImage
              src={require('assets/images/img_search-noresults.svg')}
            />
          }
        />
      ) : (
        <EmptyState
          title="No transactions paid out yet."
          subtitle="When you receive your first payout, the paid transactions will appear here."
          small
          image={
            <EmptyStateIllustrationImage
              src={require('assets/images/img_job-completed.svg')}
            />
          }
        />
      )}
    </TableEmptyStateContainer>
  )
}
